import type { IMBOImageUrl, IMBOImageUrlWithTransform } from '@vgtv/api-client';
import memoize from 'lodash/memoize';

import type { ImagePreset } from './images/presets';
import { imagesTransforms, defaultImagesTransforms } from './images/transforms';
import { getImageSizes } from './images/getImageSizes';

export type ImageAspectRatio =
  | 'vertical' // 3:2
  | 'horizontal' // 16:9
  | 'square' // 1:1
  | `${number}/${number}`; // custom

export type ImageSize = 'large' | 'medium';

function getImageSrcSet(
  imageUrl: IMBOImageUrl,
  imagePreset: ImagePreset
): string | undefined {
  return imagesTransforms[imagePreset as keyof typeof imagesTransforms]
    ? imagesTransforms[imagePreset as keyof typeof imagesTransforms]
        .map((transform) => `${imageUrl}?t[]=${transform}`)
        .join(', ')
    : undefined;
}

function getImageDefaultSrc(
  imageUrl: IMBOImageUrl,
  imagePreset: ImagePreset
): string | undefined {
  return defaultImagesTransforms[
    imagePreset as keyof typeof defaultImagesTransforms
  ]
    ? `${imageUrl}?t[]=${
        defaultImagesTransforms[
          imagePreset as keyof typeof defaultImagesTransforms
        ]
      }`
    : undefined;
}

export interface ImageSources {
  /**
   * base url with no transform
   */
  imageUrl?: IMBOImageUrl;
  /**
   * url with the default transform applied (use as the src attribute for an image)
   */
  src?: string;
  /**
   * a set of multiple {@link IMBOImageUrlWithTransform} combined with their intrinsic width in pixels {@link https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images|see MDN documentation}
   */
  srcSet?: IMBOImageUrlWithTransform;
  /**
   * a set media conditions for choosing the right image files from the given srcSet
   */
  sizes?: string;
  aspectRatio: ImageAspectRatio;
}

const getImageSources = memoize(
  ({
    imageUrl,
    imagePreset,
    aspectRatio = 'horizontal',
    size,
  }: {
    imageUrl?: IMBOImageUrl;
    imagePreset: ImagePreset;
    aspectRatio?: ImageAspectRatio;
    size?: ImageSize;
  }): ImageSources => {
    return {
      imageUrl,
      src: imageUrl ? getImageDefaultSrc(imageUrl, imagePreset) : undefined,
      srcSet: imageUrl ? getImageSrcSet(imageUrl, imagePreset) : undefined,
      sizes: getImageSizes(imagePreset, size),
      aspectRatio,
    };
  }
);

export { getImageSrcSet, getImageDefaultSrc, getImageSources };
